/* eslint-disable indent */
import React, { useEffect } from 'react';
import {
  FormData,
  FormErrors,
  IUpsertPlannedActivityDialog,
} from './interfaces';
import {
  AppBar,
  Button,
  CustomTheme,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SelectField } from '../../../../components/forms/SelectField';
import { atLeast, isEmpty } from '../../../../helpers/formHelpers';

import { LoadingLayover } from '../../../../components/LoadingLayover';
import { enqueueSnackbar } from 'notistack';
import { useGetAgentsList } from '../../../../reactQuery/Agents/agents';
import { useNavigate } from 'react-router';
import { MutateOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useFormState from '../../../../hooks/useFormState';
import useTrackFormChanges from '../../../../hooks/useTrackFormChanges';
import { useGetClientsList } from '../../../../reactQuery/clients';
import { TagsField } from '../../../../components/forms/TagsField';
import dayjs, { Dayjs } from 'dayjs';
import { isTodayOrFuture, isValidDate } from '../../../../helpers/dateHelpers';
import { useAuth } from '../../../../context/AuthProvider';
import {
  useGetClientContactsList,
  useGetContactsList,
} from '../../../../reactQuery/contacts';
import { ROUTES } from '../../../../routes/Routes';
import {
  DatePicker,
  TimePicker,
  TimeRangePicker,
} from '../../../../components/forms';
import { capitalizeStr } from '../../../../helpers/stringHelpers';
import {
  useCreatePlannedActivityMutation,
  useGetActions,
  useUpdatePlannedActivityMutation,
} from '../../../../reactQuery/Activities/activities';

export const UpsertPlannedActivityDialog: React.FC<
  IUpsertPlannedActivityDialog
> = ({ plannedActivity, show, onClose }): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { auth } = useAuth();
  const currentUser = auth?.user;
  const timeTypeOptions: FormData['timeType'][] = ['no-time', 'exact', 'range'];

  const {
    mutate: createPlannedActivity,
    isLoading: isCreatingPlannedActivity,
  } = useCreatePlannedActivityMutation();
  const {
    mutate: updatePlannedActivity,
    isLoading: isUpdatingPlannedActivity,
  } = useUpdatePlannedActivityMutation();

  const theme = useTheme<CustomTheme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('tablet'));
  const isEditMode = !!plannedActivity;

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>({
    timeType: 'no-time',
  });
  const { formErrors, setFormErrors } = useTrackFormChanges<FormData>(formData);

  const {
    data: agentsList,
    isLoading: loadingAgentsList,
    error: agentsListError,
  } = useGetAgentsList(show);

  const {
    data: clientsList,
    isLoading: loadingClientsList,
    error: clientsListError,
  } = useGetClientsList();

  const { data: contactsAsList, error: contactsListError } = useGetContactsList(
    !!plannedActivity
  );

  const { data: contactsOfClientList, error: contactsOfClientListError } =
    useGetClientContactsList(formData.clientId, !plannedActivity);

  const contactsList = plannedActivity ? contactsAsList : contactsOfClientList;

  const {
    data: actionsList,
    isLoading: loadingActionsList,
    error: actionsListError,
  } = useGetActions();

  const isLoadingListOptions =
    loadingAgentsList || loadingClientsList || loadingActionsList;

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.title || isEmpty(formData.title)) {
      errors.title = t('errors.required');
    } else if (!atLeast(formData.title, 10)) {
      errors.title = t('errors.atLeast', {
        field: t('entities:plannedActivity.title').toLowerCase(),
        min: 10,
      });
    }

    if (!formData.description || isEmpty(formData.description)) {
      errors.description = t('errors.required');
    } else if (!atLeast(formData.description, 10)) {
      errors.description = t('errors.atLeast', {
        field: t('entities:plannedActivity.description').toLowerCase(),
        min: 10,
      });
    }

    if (formData.performAt === null || formData.performAt === undefined) {
      errors.performAt = t('errors.required');
    } else if (!isValidDate(formData.performAt)) {
      errors.performAt = t('errors.invalidDate');
    } else if (!isEditMode && !isTodayOrFuture(formData.performAt, true)) {
      errors.performAt = t('errors.dateNeedsToBeTodayOrInTheFuture');
    }

    if (formData.timeType === 'exact' && !formData.startTime) {
      errors.startTime = t('errors.required');
    }

    if (formData.timeType === 'range' && !formData.startTime) {
      errors.startTime = t('errors.required');
    }

    if (formData.timeType === 'range' && !formData.endTime) {
      errors.endTime = t('errors.required');
    }

    if (formData.clientId === undefined) {
      errors.clientId = t('errors.pleaseSelectAnOption');
    }

    if (formData.actionTags?.length === 0) {
      errors.actionTags = t('errors.pleaseSelectAnOption');
    }

    if (formData.agentId === undefined) {
      errors.agentId = t('errors.pleaseSelectAnOption');
    }

    setFormErrors(errors);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!Object.keys(formErrors).length) {
      const plannedActivityData = {
        title: formData.title as string,
        description: formData.description as string,
        clientId: formData.clientId as number,
        actionId: formData.actionTags?.length ? formData.actionTags[0] : 0,
        agentId: formData.agentId as number,
        performAt: formData.performAt as Dayjs,
        contacts: (formData.contacts || []) as number[],
        startTime: formData.timeType !== 'no-time' ? formData.startTime : null,
        endTime: formData.timeType === 'range' ? formData.endTime : null,
      };

      const mutateOptions:
        | MutateOptions<number, AxiosError, unknown>
        | undefined = {
        onSuccess: (plannedActivityId) => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updatePlannedActivity.success')
              : t('forms:createPlannedActivity.success'),
            {
              variant: 'success',
            }
          );

          navigate(ROUTES.activities.details(plannedActivityId));
          onClose();
        },
        onError: () => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updatePlannedActivity.failed')
              : t('forms:createPlannedActivity.failed'),
            {
              variant: 'error',
            }
          );
        },
      };

      if (isEditMode) {
        updatePlannedActivity(
          {
            plannedActivityId: plannedActivity.id,
            plannedActivityData,
          },
          mutateOptions
        );
      } else {
        createPlannedActivity(plannedActivityData, mutateOptions);
      }
    }
  };

  useEffect(() => {
    const error =
      actionsListError ||
      agentsListError ||
      clientsListError ||
      contactsListError ||
      contactsOfClientListError;

    if (error) {
      enqueueSnackbar(t('errors.somethingWentWrong'), {
        variant: 'error',
      });

      onClose();
    }
  }, [agentsListError]);

  useEffect(() => {
    setFormErrors({});

    if (plannedActivity) {
      let timeType: FormData['timeType'] = 'no-time';

      if (plannedActivity.startTime && plannedActivity.endTime) {
        timeType = 'range';
      } else if (plannedActivity.startTime) {
        timeType = 'exact';
      }

      setFormData({
        performAt: dayjs(plannedActivity.performAt),
        clientId: plannedActivity.client.id,
        actionTags: [plannedActivity.action.id],
        agentId: plannedActivity.agent.id,
        title: plannedActivity.title,
        description: plannedActivity.description,
        contacts: plannedActivity.contacts?.map((contact) => contact.id),
        timeType: timeType,
        startTime: plannedActivity.startTime
          ? dayjs(plannedActivity.startTime, 'HH:mm')
          : null,
        endTime: plannedActivity.endTime
          ? dayjs(plannedActivity.endTime, 'HH:mm')
          : null,
      });
    } else {
      setFormData({
        performAt: dayjs(),
        agentId: currentUser?.id,
        timeType: 'no-time',
      });
    }
  }, [show]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={show}
      keepMounted={false}
      //   TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            {isEditMode
              ? t('forms:updatePlannedActivity.title')
              : t('forms:createPlannedActivity.title')}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            disabled={isCreatingPlannedActivity || isUpdatingPlannedActivity}
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        dividers={true}
        sx={{
          minWidth: fullScreen ? undefined : '400px',
        }}
      >
        {isLoadingListOptions ? (
          <LoadingLayover height="200px" />
        ) : (
          <Stack
            gap="15px"
            component="form"
            id="createPlannedActivityForm"
            onSubmit={handleSubmit}
          >
            <DatePicker
              label={t('entities:plannedActivity.performAt')}
              onChange={(date) => setFormFieldData('performAt', date)}
              selectedDate={formData.performAt}
              error={!!formErrors.performAt}
              errorMessage={formErrors.performAt}
              disablePast={!isEditMode}
            />

            <Stack
              sx={{
                width: fullScreen ? undefined : '400px',
                gap: '10px',
                border:
                  formData.timeType !== 'no-time'
                    ? '1px solid #C4C4C4'
                    : undefined,
                padding:
                  formData.timeType !== 'no-time' ? '12px 5px 10px' : undefined,
                boxSizing: 'border-box',
                borderRadius: '4px',
              }}
            >
              <SelectField
                name="time-type"
                label={t('forms:createPlannedActivity.fields.timeType.label')}
                value={formData.timeType}
                onChange={(event: SelectChangeEvent) =>
                  setFormFieldData(
                    'timeType',
                    event.target.value as FormData['timeType']
                  )
                }
                error={!!formErrors.timeType}
                errorMessage={formErrors.timeType}
                required
              >
                {timeTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(
                      `forms:createPlannedActivity.fields.timeType.options.${option}`
                    )}
                  </MenuItem>
                ))}
              </SelectField>

              {formData.timeType === 'exact' && (
                <TimePicker
                  label={capitalizeStr(t('words.time'))}
                  selectedTime={formData.startTime}
                  onChange={(time) => setFormFieldData('startTime', time)}
                  error={!!formErrors.startTime}
                  errorMessage={formErrors.startTime}
                  required
                />
              )}

              {formData.timeType === 'range' && (
                <TimeRangePicker
                  value={{
                    from: formData.startTime || null,
                    to: formData.endTime || null,
                  }}
                  onChange={(timeRange) => {
                    setFormFieldData('startTime', timeRange.from);
                    setFormFieldData('endTime', timeRange.to);
                  }}
                  error={{
                    start: !!formErrors.startTime,
                    end: !!formErrors.endTime,
                  }}
                  errorMessage={{
                    start: formErrors.startTime,
                    end: formErrors.endTime,
                  }}
                  required
                />
              )}
            </Stack>

            <SelectField
              name="client"
              label={t('entities:activityLog.client')}
              value={formData.clientId ? String(formData.clientId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('clientId', Number(event.target.value))
              }
              error={!!formErrors.clientId}
              errorMessage={formErrors.clientId}
              required
            >
              {clientsList?.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </SelectField>

            <TagsField
              existingTags={contactsList?.map((contact) => {
                return {
                  id: contact.id,
                  name: `${contact.firstName} ${contact.lastName}`,
                };
              })}
              label={t('entities:activityLog.contacts')}
              value={formData.contacts || []}
              error={formErrors.contacts}
              disabled={!(contactsList && contactsList.length > 0)}
              onChange={(value) => {
                setFormFieldData('contacts', value);
              }}
              sx={{
                maxWidth: fullScreen ? undefined : '400px',
              }}
            />

            <TagsField
              existingTags={actionsList}
              label={t('entities:activityLog.action')}
              value={formData.actionTags || []}
              onChange={(value) => {
                setFormFieldData('actionTags', value);
              }}
              sx={{
                maxWidth: fullScreen ? undefined : '400px',
              }}
              maxTags={1}
              createTags
              error={formErrors.actionTags}
              required
            />

            <SelectField
              name="agent"
              label={t('entities:activityLog.agent')}
              value={formData.agentId ? String(formData.agentId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('agentId', Number(event.target.value))
              }
              error={!!formErrors.agentId}
              errorMessage={formErrors.agentId}
              required
            >
              {agentsList?.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {`${agent.firstName} ${agent.lastName}`}
                </MenuItem>
              ))}
            </SelectField>

            <TextField
              label={t('entities:plannedActivity.title')}
              variant="outlined"
              size="small"
              value={formData.title}
              placeholder={plannedActivity?.title}
              onChange={(e) => setFormFieldData('title', e.target.value)}
              error={!!formErrors.title}
              helperText={formErrors.title}
              required
            />

            <TextField
              label={t('entities:plannedActivity.description')}
              variant="outlined"
              size="small"
              value={formData.description}
              placeholder={plannedActivity?.description}
              onChange={(e) => setFormFieldData('description', e.target.value)}
              error={!!formErrors.description}
              helperText={formErrors.description}
              multiline
              minRows={4}
              maxRows={10}
              required
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isCreatingPlannedActivity || isUpdatingPlannedActivity}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          form="createPlannedActivityForm"
          type="submit"
          variant="contained"
          onClick={validateForm}
          disabled={
            isLoadingListOptions ||
            isCreatingPlannedActivity ||
            isUpdatingPlannedActivity
          }
        >
          {isEditMode ? t('buttons.update') : t('buttons.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
